import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions, DialogContent } from '@mui/material';
import { SERVICE_AREA_TYPES } from 'admin/userRelations/userRelationUtils';
import { StyledDialog } from 'common/components/StyledDialog/StyledDialog';
import { StyledDialogTitle } from 'common/components/StyledDialog/StyledDialogTitle';
import { Checkbox, FormCheckBoxGroup } from 'common/form/components/FormCheckboxGroup/FormCheckboxGroup';
import { DialogComponentProps } from 'common/hooks/useDialog';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import {
  ServiceAreaType,
  useAdminGetStaffQuery,
  useAdminUpdateStaffMutation,
  useCurrentUserQuery,
} from 'generated/graphql';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { StyledDialogFormError } from '../../../common/components/StyledDialog/StyledDialogFormError';

export interface FormValues {
  serviceAreaChecks: (boolean | undefined)[];
}

export type ChangeServiceAreasDialogProps = DialogComponentProps<void>;

export const ChangeServiceAreasDialog: React.FC<ChangeServiceAreasDialogProps> = ({ isOpen, onReject, onResolve }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: loadingAuth } = useContext(AuthContext);
  const { data: currentUserData, loading: loadingCurrentUser } = useCurrentUserQuery();
  const { data: staffData, loading: loadingStaff } = useAdminGetStaffQuery({
    skip: !isAuthenticated,
    variables: { id: currentUserData?.user?.staffId ?? '' },
  });

  const staff = staffData?.adminGetStaff;

  const [adminUpdateStaff, { error, loading }] = useAdminUpdateStaffMutation({});

  const serviceAreas = SERVICE_AREA_TYPES.sort((a, b) => a.name.localeCompare(b.name));

  const schema: yup.SchemaOf<FormValues> = yup.object({
    serviceAreaChecks: yup.array(),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      serviceAreaChecks: [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (staff) {
      formMethods.setValue(
        'serviceAreaChecks',
        serviceAreas?.map((serviceArea) => staff.serviceAreas?.includes(serviceArea.id as ServiceAreaType)),
      );
    }
  }, [staff, serviceAreas, formMethods]);

  if (loadingAuth || loadingCurrentUser || loadingStaff || !staff) return null;

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    if (data.serviceAreaChecks) {
      const resp = await adminUpdateStaff({
        variables: {
          id: staff.id,
          input: {
            serviceAreas:
              (data.serviceAreaChecks
                ?.map((checked, index) => (checked ? serviceAreas?.[index].id : null))
                .filter((val) => val != null) as ServiceAreaType[]) ?? [],
            serviceLocationIds: staff.serviceLocations?.map((serviceLocation) => serviceLocation.id) ?? [],
          },
        },
      });

      if (resp) {
        onResolve();
      }
    }
  });

  const options: Checkbox[] =
    serviceAreas?.map((serviceArea, index) => ({
      label: serviceArea.name,
      name: index.toString(),
    })) ?? [];

  return (
    <FormProvider {...formMethods}>
      <StyledDialog maxWidth="lg" onClose={() => onReject()} open={isOpen}>
        <StyledDialogTitle textAlign="left">{t('settings.changeDialog.serviceAreas.title')}</StyledDialogTitle>
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <DialogContent>
            <FormCheckBoxGroup
              label={t('settings.changeDialog.serviceAreas.label')}
              name="serviceAreaChecks"
              options={options}
            />
          </DialogContent>
          <DialogActions>
            <Button color="secondaryButton" onClick={() => onReject()}>
              {t('settings.changeDialog.cancel')}
            </Button>
            <LoadingButton loading={loading} type="submit">
              {t('settings.changeDialog.save')}
            </LoadingButton>
          </DialogActions>
          {!!error && !loading && (
            <StyledDialogFormError content={error.message} title={t('settings.changeDialog.error')} />
          )}
        </Box>
      </StyledDialog>
    </FormProvider>
  );
};
